import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MockOrder, MOCK_ORDERS } from '../mock-data/mock-orders.data';

/**
 * Row Grouping Example
 * Demonstrates data grouping by multiple fields with expand/collapse functionality
 */
@Component({
  selector: 'app-row-grouping-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Row Grouping (Agrupación de Filas)</h2>
        <p class="text-gray-600 mb-2">
          El Row Grouping permite agrupar datos por uno o múltiples campos, creando una estructura jerárquica con grupos expandibles/colapsables.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            📊 Multi-Level Grouping
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            🔽 Expand/Collapse Groups
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            🏷️ Group Panel
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            🧮 Aggregates (Sum, Avg, Count)
          </span>
          <span class="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold">
            📏 Visual Indentation
          </span>
        </div>

        <!-- Control Buttons -->
        <div class="flex gap-2 mb-4 flex-wrap">
          <button mat-raised-button color="primary" (click)="expandAll()">
            <mat-icon>unfold_more</mat-icon>
            Expandir Todo
          </button>
          <button mat-raised-button color="accent" (click)="collapseAll()">
            <mat-icon>unfold_less</mat-icon>
            Colapsar Todo
          </button>
          <button mat-raised-button (click)="clearGrouping()">
            <mat-icon>clear</mat-icon>
            Limpiar Agrupación
          </button>
        </div>

        <!-- Grouping Presets -->
        <div class="mb-4">
          <h3 class="font-semibold mb-2">⚡ Presets de Agrupación:</h3>
          <div class="flex gap-2 flex-wrap">
            <button
              mat-raised-button
              [color]="currentPreset === 'status' ? 'primary' : 'basic'"
              (click)="groupByStatus()">
              <mat-icon>flag</mat-icon>
              Por Estado
            </button>
            <button
              mat-raised-button
              [color]="currentPreset === 'warehouse' ? 'primary' : 'basic'"
              (click)="groupByWarehouse()">
              <mat-icon>warehouse</mat-icon>
              Por Almacén
            </button>
            <button
              mat-raised-button
              [color]="currentPreset === 'status-warehouse' ? 'primary' : 'basic'"
              (click)="groupByStatusAndWarehouse()">
              <mat-icon>layers</mat-icon>
              Estado → Almacén
            </button>
            <button
              mat-raised-button
              [color]="currentPreset === 'warehouse-status' ? 'primary' : 'basic'"
              (click)="groupByWarehouseAndStatus()">
              <mat-icon>layers</mat-icon>
              Almacén → Estado
            </button>
            <button
              mat-raised-button
              [color]="currentPreset === 'customer' ? 'primary' : 'basic'"
              (click)="groupByCustomer()">
              <mat-icon>person</mat-icon>
              Por Cliente
            </button>
          </div>
        </div>

        <!-- Current Grouping Info -->
        <div class="p-4 bg-blue-50 dark:bg-blue-900 rounded mb-4" *ngIf="currentGrouping.length > 0">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            <mat-icon class="text-blue-600">info</mat-icon>
            Agrupación Actual:
          </h3>
          <div class="text-sm">
            <strong>Campos:</strong> {{ currentGrouping.join(' → ') }}
          </div>
        </div>
      </div>

      <!-- Grid -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          #grid
          [data]="ordersData"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">✨ Características del Row Grouping:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>Agrupación Multi-Nivel:</strong> Agrupa por múltiples campos en jerarquía (ej: Estado → Almacén)</li>
          <li><strong>Expand/Collapse Individual:</strong> Click en cada grupo para expandir/colapsar</li>
          <li><strong>Expand/Collapse Global:</strong> Controles para expandir/colapsar todos los grupos</li>
          <li><strong>Group Panel:</strong> Panel visual mostrando campos activos con chips removibles</li>
          <li><strong>Contador de Items:</strong> Cada grupo muestra cantidad de items que contiene</li>
          <li><strong>Agregados (Futura Feature):</strong> Sum, Avg, Min, Max, Count por grupo</li>
          <li><strong>Indentación Visual:</strong> Indentación configurable por nivel de agrupación</li>
          <li><strong>Type-Safe API:</strong> Full TypeScript support con NestedKeyOf&lt;T&gt;</li>
          <li><strong>Labels Humanizados:</strong> Conversión automática de field names a labels legibles</li>
          <li><strong>Configuración Flexible:</strong> defaultGroupBy, fieldLabels, allowUngroup, etc.</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">⚙️ Configuración del Row Grouping:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{configExample}}</code></pre>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">🎯 API Programático:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{apiExample}}</code></pre>
      </div>
    </div>
  `,
})
export class RowGroupingExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  // Current preset and grouping
  currentPreset: string = 'status-warehouse';
  currentGrouping: string[] = ['Estado', 'Almacén'];

  // ============================================================================
  // DATA
  // ============================================================================

  ordersData: MockOrder[] = MOCK_ORDERS;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nº Orden',
      width: 150,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 220,
    },
    {
      field: 'warehouse.name',
      headerName: 'Almacén',
      width: 180,
      filterable: true,
    },
    {
      field: 'statusId',
      headerName: 'Estado',
      width: 150,
      filterable: true,
      valueFormatter: (value) => {
        const statusMap: Record<number, string> = {
          0: 'Anulado',
          1: 'Activo',
          6: 'En Progreso',
          7: 'Aprobado',
          8: 'Rechazado',
          18: 'Inactivo',
        };
        return statusMap[value] || `Estado ${value}`;
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toLocaleString()}`,
      cellClass: 'text-right font-semibold',
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 120,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    grouping: {
      enabled: true,
      defaultGroupBy: ['statusId', 'warehouse.name'],
      showGroupPanel: true,
      allowUngroup: true,
      indentSize: 20,
      showAggregates: true,
      fieldLabels: {
        'statusId': 'Estado',
        'warehouse.name': 'Almacén',
        'soldTo.name': 'Cliente',
        'createdAt': 'Fecha',
      },
      panelText: 'Arrastra columnas aquí para agrupar',
    },
    pagination: {
      enabled: true,
      pageSize: 50,
      pageSizeOptions: [25, 50, 100, 200],
    },
    sorting: {
      enabled: true,
      multiColumn: true,
    },
    search: {
      enabled: true,
      showSearchInput: true,
      placeholder: 'Buscar orden, cliente...',
      searchableFields: ['serial', 'soldTo.name', 'warehouse.name'],
    },
    columnFilters: {
      enabled: true,
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
    },
    styling: {
      maxHeight: '600px',
    },
  };

  // ============================================================================
  // METHODS
  // ============================================================================

  expandAll(): void {
    if (this.grid) {
      this.grid.expandAllGroups();
    }
  }

  collapseAll(): void {
    if (this.grid) {
      this.grid.collapseAllGroups();
    }
  }

  clearGrouping(): void {
    if (this.grid && this.grid.api) {
      this.grid.api.clearGrouping();
      this.currentPreset = '';
      this.currentGrouping = [];
    }
  }

  groupByStatus(): void {
    if (this.grid && this.grid.api) {
      this.grid.api.setGroupBy(['statusId' as any]);
      this.currentPreset = 'status';
      this.currentGrouping = ['Estado'];
    }
  }

  groupByWarehouse(): void {
    if (this.grid && this.grid.api) {
      this.grid.api.setGroupBy(['warehouse.name' as any]);
      this.currentPreset = 'warehouse';
      this.currentGrouping = ['Almacén'];
    }
  }

  groupByStatusAndWarehouse(): void {
    if (this.grid && this.grid.api) {
      this.grid.api.setGroupBy(['statusId' as any, 'warehouse.name' as any]);
      this.currentPreset = 'status-warehouse';
      this.currentGrouping = ['Estado', 'Almacén'];
    }
  }

  groupByWarehouseAndStatus(): void {
    if (this.grid && this.grid.api) {
      this.grid.api.setGroupBy(['warehouse.name' as any, 'statusId' as any]);
      this.currentPreset = 'warehouse-status';
      this.currentGrouping = ['Almacén', 'Estado'];
    }
  }

  groupByCustomer(): void {
    if (this.grid && this.grid.api) {
      this.grid.api.setGroupBy(['soldTo.name' as any]);
      this.currentPreset = 'customer';
      this.currentGrouping = ['Cliente'];
    }
  }

  // ============================================================================
  // EXAMPLE CODE
  // ============================================================================

  configExample = `// Grid Configuration
gridConfig: GridConfig<Order> = {
  grouping: {
    enabled: true,                        // Enable row grouping
    defaultGroupBy: ['statusId', 'warehouse.name'],  // Default grouping fields
    showGroupPanel: true,                 // Show panel with active groups
    allowUngroup: true,                   // Allow removing groups
    indentSize: 20,                       // Indentation per level (pixels)
    showAggregates: true,                 // Show aggregate values (sum, avg, etc.)
    fieldLabels: {                        // Custom labels for fields
      'statusId': 'Estado',
      'warehouse.name': 'Almacén',
      'soldTo.name': 'Cliente',
    },
    panelText: 'Arrastra columnas aquí para agrupar',  // Panel placeholder
  },
  pagination: {
    enabled: true,                        // Pagination works with grouping
  },
  sorting: {
    enabled: true,                        // Sorting works with grouping
  },
  search: {
    enabled: true,                        // Search works with grouping
  },
};`;

  apiExample = `// Programmatic Control via GridAPI

// Set grouping fields
grid.api.setGroupBy(['statusId', 'warehouse.name']);

// Get current grouping
const currentGroups = grid.api.getGroupBy();  // ['statusId', 'warehouse.name']

// Clear all grouping
grid.api.clearGrouping();

// Expand/Collapse all groups
grid.expandAllGroups();
grid.collapseAllGroups();

// The grouping is hierarchical:
// Level 0: Groups by 'statusId' (Activo, En Progreso, Aprobado, etc.)
// Level 1: Within each status, groups by 'warehouse.name'
// Level 2: Individual order rows

// Example hierarchy:
// └─ Activo (60 items)
//     ├─ Almacén Tegucigalpa (25 items)
//     │   ├─ PED-2025-000001
//     │   ├─ PED-2025-000002
//     │   └─ ...
//     └─ Almacén San Pedro Sula (35 items)
//         ├─ PED-2025-000015
//         └─ ...`;
}
